import { useState } from 'react';
import { Info } from 'lucide-react';

import type { Configuracao } from '@/api/secretario';
import { Alert, AlertDescription } from '@/components/ui/alert';

import { useAdvogadoEmEdicao } from './advogadoEmEdicao';
import { AreasManager } from './AreasManager';
import { BaseUniversalCard } from './BaseUniversalCard';
import { HonorariosGeraisCard } from './HonorariosGeraisCard';
import { temPreco } from './HonorariosCampos';
import { ModelosPadrao } from './ModelosPadrao';

type Secao = 'honorarios' | 'modelos' | 'base' | null;

/**
 * O modo personalização da aba Áreas: o que faz o Secretário ir além do
 * pré-atendimento — honorários e modelos (contrato e procuração) — e o que ele
 * pergunta e pede em cada área. Nada aqui bloqueia a ativação
 * (docs/PLANO_SECRETARIO_ATIVACAO_SIMPLES.md).
 *
 * As seções pesadas são recolhíveis e avançam sozinhas: salvar os honorários abre
 * os modelos; salvar os modelos abre a base.
 */
export function PersonalizarAreas({ config }: { config: Configuracao }) {
  const [aberto, setAberto] = useState<Secao>(() => {
    if (!temPreco(config.honorarios)) return 'honorarios';
    if (!config.template_contrato_id || !config.template_procuracao_id) return 'modelos';
    return null;
  });
  const [baseConcluida, setBaseConcluida] = useState(false);
  // A base (o que se pede em toda área) é do Secretário inteiro, não de um advogado:
  // no escritório ela fica na aba Advogados, para todos.
  const advogado = useAdvogadoEmEdicao();

  const ativas = config.prontidao.por_area;
  const fechamContrato = ativas.filter((a) => a.pronta).length;

  return (
    <div className="space-y-4">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          {fechamContrato === 0
            ? 'Hoje o Secretário faz o pré-atendimento: ouve o caso, recolhe os documentos e passa o atendimento para você. Informe seus honorários e escolha os modelos para ele também fechar contrato e procuração.'
            : fechamContrato === ativas.length
              ? 'O Secretário fecha contrato e procuração em todas as áreas.'
              : `O Secretário fecha contrato em ${fechamContrato} de ${ativas.length} áreas; nas demais, faz o pré-atendimento. Veja abaixo o que falta em cada uma.`}
        </AlertDescription>
      </Alert>

      <HonorariosGeraisCard
        config={config}
        open={aberto === 'honorarios'}
        onOpenChange={(o) => setAberto(o ? 'honorarios' : null)}
        onSaved={() => setAberto('modelos')}
      />
      <ModelosPadrao
        config={config}
        open={aberto === 'modelos'}
        onOpenChange={(o) => setAberto(o ? 'modelos' : null)}
        onSaved={() => setAberto('base')}
      />
      {!advogado && (
        <BaseUniversalCard
          config={config}
          open={aberto === 'base'}
          onOpenChange={(o) => setAberto(o ? 'base' : null)}
          concluida={baseConcluida}
          onConcluir={() => {
            setBaseConcluida(true);
            setAberto(null);
          }}
        />
      )}
      <AreasManager config={config} />
    </div>
  );
}
